import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X } from "lucide-react";

export default function DropZone({
  file,
  onChange,
  accept = ".xlsx",
  testId = "dropzone",
  hint = "Sleep een Excel bestand hierheen of klik om te selecteren",
}) {
  const [isOver, setIsOver] = useState(false);
  const inputRef = useRef(null);
  const acceptList = accept.split(",").map((s) => s.trim().toLowerCase());

  const validateAndSet = useCallback((f) => {
    if (!f) { onChange(null); return; }
    const name = f.name.toLowerCase();
    if (!acceptList.some((ext) => name.endsWith(ext))) {
      const err = new Error(`Onjuist bestandstype. Verwacht: ${accept}`);
      throw err;
    }
    onChange(f);
  }, [acceptList, accept, onChange]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) { try { validateAndSet(f); } catch (_) {} }
  }, [validateAndSet]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      data-testid={`${testId}-drop`}
      className={`relative cursor-pointer rounded-md border-2 border-dashed px-6 py-10 text-center transition-all ${
        isOver
          ? "border-aramfix-red bg-aramfix-red/5"
          : file
            ? "border-nibro-sage-dark bg-nibro-sage-pale"
            : "border-zinc-300 bg-zinc-50 hover:border-nibro-sage hover:bg-nibro-sage-pale"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            try { validateAndSet(f); }
            catch (_) { e.target.value = ""; }
          }
        }}
        data-testid={`${testId}-input`}
      />
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileSpreadsheet size={28} className="text-nibro-sage-dark" />
          <div className="text-left">
            <div className="font-mono text-sm">{file.name}</div>
            <div className="font-mono text-[10px] text-zinc-500">
              {(file.size / 1024).toFixed(1)} KB · klaar om te uploaden
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            data-testid={`${testId}-clear`}
            className="ml-3 rounded p-1 hover:bg-zinc-100"
            aria-label="Bestand verwijderen"
          >
            <X size={16} className="text-zinc-500" />
          </button>
        </div>
      ) : (
        <>
          <UploadCloud size={40} className="mx-auto mb-3 text-zinc-400" />
          <div className="font-mono text-sm text-zinc-700">
            {isOver ? "Laat los om te selecteren" : hint}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            {accept.replaceAll(".", "").toUpperCase()}
          </div>
        </>
      )}
    </div>
  );
}
